import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Factory,
  Upload,
  Users,
  Mail,
  Target,
  TrendingUp,
  Globe,
  AlertCircle,
  ArrowRight,
  Clock,
  RefreshCw,
  Filter,
  X,
  Package,
  Shirt,
  Anchor,
  Ship,
  ExternalLink,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const PIPELINE_LABELS = {
  raw_imported: "Raw Imported",
  profile_enriched: "Profile Enriched",
  contact_identified: "Contact Identified",
  outreach_sent: "Outreach Sent",
  response_received: "Response Received",
  requirement_identified: "Requirement Identified",
  qualified_lead: "Qualified Lead",
  converted: "Converted",
  lost: "Lost"
};

const ExportDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("all");
  
  // Port modal state
  const [selectedPort, setSelectedPort] = useState(null);
  const [portType, setPortType] = useState("loading"); // "loading" or "discharge"
  const [portProducts, setPortProducts] = useState([]);
  const [portSummary, setPortSummary] = useState(null);
  const [loadingPort, setLoadingPort] = useState(false);
  const [showPortModal, setShowPortModal] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [selectedCountry]);

  const fetchCountries = async () => {
    try {
      // First fetch without filter to get all countries
      const response = await axios.get(`${API}/export/dashboard`);
      if (response.data.by_country) {
        const countryList = response.data.by_country
          .map(c => c._id || c.country)
          .filter(Boolean)
          .sort();
        setCountries(countryList);
      }
    } catch (error) {
      console.error("Failed to fetch countries");
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const params = selectedCountry !== "all" ? { country: selectedCountry } : {};
      const response = await axios.get(`${API}/export/dashboard`, { params });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setSelectedCountry("all");
  };

  const fetchPortProducts = async (portName, type) => {
    setLoadingPort(true);
    try {
      const encodedPort = encodeURIComponent(portName);
      const response = await axios.get(`${API}/export/ports/${encodedPort}/products`, {
        params: { port_type: type }
      });
      setPortProducts(response.data.products || []);
      setPortSummary(response.data.summary || null);
      setSelectedPort(portName);
      setPortType(type);
      setShowPortModal(true);
    } catch (error) {
      toast.error("Failed to load port details");
      console.error(error);
    } finally {
      setLoadingPort(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="export-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Export Dashboard</h1>
          <p className="text-slate-500">
            Factory intelligence and outreach overview
            {selectedCountry !== "all" && (
              <span className="text-blue-600 font-medium ml-1">• Filtered by {selectedCountry}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Country Filter */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-500" />
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-[180px]" data-testid="country-filter">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCountry !== "all" && (
              <Button variant="ghost" size="sm" onClick={clearFilter} className="h-8 px-2">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button onClick={() => navigate('/export/imports')} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Upload className="w-4 h-4" />
            Import Shipments
          </Button>
        </div>
      </div>

      {/* Country Filter Banner (when filtered) */}
      {selectedCountry !== "all" && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Viewing data for: {selectedCountry}</p>
                  <p className="text-sm text-slate-500">All metrics below are filtered by this country</p>
                </div>
              </div>
              <Button variant="outline" onClick={clearFilter} className="gap-2">
                <X className="w-4 h-4" />
                Show All Countries
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Factory className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_factories?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-500">Total Factories</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_shipments?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-500">Total Shipments</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.pipeline_counts?.outreach_sent || 0}</p>
                <p className="text-sm text-slate-500">Outreach Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.pipeline_counts?.converted || 0}</p>
                <p className="text-sm text-slate-500">Converted</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top 10 Fabrics/Products Section - MOVED TO TOP */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shirt className="w-5 h-5 text-purple-600" />
            Top 10 Fabrics / Products Imported
            {selectedCountry !== "all" && (
              <Badge variant="outline" className="ml-2 bg-purple-50 text-purple-700 border-purple-200">
                {selectedCountry}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.top_products?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Product / Fabric Description</TableHead>
                  <TableHead className="text-right">Shipments</TableHead>
                  <TableHead className="text-right">Factories</TableHead>
                  <TableHead className="text-right">Total Quantity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.top_products.map((product, idx) => (
                  <TableRow key={idx} className="hover:bg-slate-50">
                    <TableCell>
                      <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium max-w-lg">
                      <p className="line-clamp-2 text-sm">{product.product}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{product.shipment_count?.toLocaleString()}</Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-600">
                      {product.factory_count}
                    </TableCell>
                    <TableCell className="text-right text-slate-600">
                      {product.total_qty > 0 ? (
                        <span>{product.total_qty?.toLocaleString()} {product.unit || ''}</span>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No product data available</p>
              <p className="text-xs mt-1">Import shipment data to see top fabrics</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Summary */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Pipeline Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(PIPELINE_LABELS).slice(0, 6).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full"
                        style={{ 
                          width: `${Math.min(100, ((stats?.pipeline_counts?.[key] || 0) / (stats?.total_factories || 1)) * 100)}%` 
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-900 w-8 text-right">
                      {stats?.pipeline_counts?.[key] || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4 gap-2"
              onClick={() => navigate('/export/pipeline')}
            >
              View Full Pipeline
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Country Breakdown */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Factories by Country
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.country_breakdown || {}).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([country, count]) => (
                <div 
                  key={country} 
                  className="flex items-center justify-between cursor-pointer hover:bg-slate-50 p-1 rounded"
                  onClick={() => setSelectedCountry(country)}
                >
                  <span className="text-sm text-slate-600">{country}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
              {Object.keys(stats?.country_breakdown || {}).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No factories imported yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Port Statistics Row - MOVED TO BOTTOM */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Loading Ports */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Ship className="w-5 h-5 text-orange-600" />
              Top Loading Ports (Origin)
              {selectedCountry !== "all" && (
                <Badge variant="outline" className="ml-2 text-xs bg-orange-50 text-orange-700">{selectedCountry}</Badge>
              )}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Click a port to see products shipped from there</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.top_loading_ports?.slice(0, 8).map((port, idx) => (
                <div 
                  key={port._id} 
                  className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors group"
                  onClick={() => fetchPortProducts(port._id, 'loading')}
                  data-testid={`loading-port-${idx}`}
                >
                  <span className="w-5 h-5 bg-orange-100 text-orange-700 rounded text-xs flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-slate-700 flex-1 truncate group-hover:text-orange-700">{port._id}</span>
                  <Badge variant="outline" className="bg-orange-50">{port.shipment_count?.toLocaleString()}</Badge>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-orange-500 transition-colors" />
                </div>
              ))}
              {(!stats?.top_loading_ports || stats.top_loading_ports.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No port data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Discharge Ports */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Anchor className="w-5 h-5 text-teal-600" />
              Top Discharge Ports (Destination)
              {selectedCountry !== "all" && (
                <Badge variant="outline" className="ml-2 text-xs bg-teal-50 text-teal-700">{selectedCountry}</Badge>
              )}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Click a port to see products arriving there</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.top_discharge_ports?.slice(0, 8).map((port, idx) => (
                <div 
                  key={port._id} 
                  className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-teal-50 transition-colors group"
                  onClick={() => fetchPortProducts(port._id, 'discharge')}
                  data-testid={`discharge-port-${idx}`}
                >
                  <span className="w-5 h-5 bg-teal-100 text-teal-700 rounded text-xs flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-slate-700 flex-1 truncate group-hover:text-teal-700">{port._id}</span>
                  <Badge variant="outline" className="bg-teal-50">{port.shipment_count?.toLocaleString()}</Badge>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500 transition-colors" />
                </div>
              ))}
              {(!stats?.top_discharge_ports || stats.top_discharge_ports.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No port data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Factories */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Factory className="w-5 h-5 text-blue-600" />
              Top Factories by Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.top_factories?.slice(0, 5).map((factory, idx) => (
                <div 
                  key={factory.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => navigate(`/export/factories/${factory.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{factory.name}</p>
                      <p className="text-xs text-slate-500">{factory.country}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">{factory.shipment_count} shipments</p>
                    <p className="text-xs text-slate-500">Score: {factory.target_score}</p>
                  </div>
                </div>
              ))}
              {(!stats?.top_factories || stats.top_factories.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">Import shipment data to see top factories</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pending Tasks */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Your Pending Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.pending_tasks?.map((task) => (
                <div key={task.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{task.title}</p>
                    <p className="text-xs text-slate-500">{task.factory_name || 'No factory'}</p>
                  </div>
                  <Badge variant={task.priority === 'high' ? 'destructive' : 'secondary'}>
                    {task.priority}
                  </Badge>
                </div>
              ))}
              {(!stats?.pending_tasks || stats.pending_tasks.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No pending tasks</p>
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4 gap-2"
              onClick={() => navigate('/export/tasks')}
            >
              View All Tasks
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activities */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Recent Activities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats?.recent_activities?.slice(0, 5).map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-3 border-b border-slate-100 last:border-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs ${
                  activity.activity_type === 'email' ? 'bg-blue-500' :
                  activity.activity_type === 'call' ? 'bg-green-500' :
                  activity.activity_type === 'meeting' ? 'bg-purple-500' : 'bg-slate-400'
                }`}>
                  {activity.activity_type === 'email' ? <Mail className="w-4 h-4" /> :
                   activity.activity_type === 'call' ? '📞' : '📝'}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-900">
                    <span className="font-medium">{activity.created_by_name}</span>
                    {' '}{activity.activity_type === 'email' ? 'sent email to' : 'logged'}{' '}
                    <span className="font-medium">{activity.factory_name}</span>
                  </p>
                  <p className="text-xs text-slate-500">{activity.subject || activity.summary?.slice(0, 50)}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
            {(!stats?.recent_activities || stats.recent_activities.length === 0) && (
              <p className="text-sm text-slate-400 text-center py-4">No recent activities</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Port Products Modal */}
      <Dialog open={showPortModal} onOpenChange={setShowPortModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {portType === 'loading' ? (
                <Ship className="w-5 h-5 text-orange-600" />
              ) : (
                <Anchor className="w-5 h-5 text-teal-600" />
              )}
              Products at {selectedPort}
              <Badge 
                variant="outline" 
                className={portType === 'loading' ? 'bg-orange-50 text-orange-700' : 'bg-teal-50 text-teal-700'}
              >
                {portType === 'loading' ? 'Loading Port' : 'Discharge Port'}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {/* Port Summary Stats */}
          {portSummary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{portSummary.total_shipments?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Shipments</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{portSummary.unique_products?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Products</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{portSummary.unique_factories?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Factories</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{portSummary.unique_shippers?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Shippers</p>
              </div>
            </div>
          )}

          {/* Countries served */}
          {portSummary?.countries?.length > 0 && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="text-xs text-slate-500">Countries:</span>
              {portSummary.countries.map((country, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  <Globe className="w-3 h-3 mr-1" />
                  {country}
                </Badge>
              ))}
            </div>
          )}
          
          <div className="flex-1 overflow-y-auto mt-4">
            {loadingPort ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : portProducts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No products found for this port</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Product Description</TableHead>
                    <TableHead className="text-center">Shipments</TableHead>
                    <TableHead className="text-center">Total Qty</TableHead>
                    <TableHead className="text-center">Factories</TableHead>
                    <TableHead>HSN Codes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {portProducts.map((product, idx) => (
                    <TableRow key={idx} className="hover:bg-slate-50">
                      <TableCell>
                        <span className={`w-6 h-6 ${portType === 'loading' ? 'bg-orange-100 text-orange-700' : 'bg-teal-100 text-teal-700'} rounded-full flex items-center justify-center text-xs font-bold`}>
                          {idx + 1}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium max-w-sm">
                        <p className="line-clamp-2 text-sm">{product.product}</p>
                        {product.top_factories?.length > 0 && (
                          <p className="text-xs text-slate-400 mt-1 truncate">
                            Top: {product.top_factories.slice(0, 2).join(', ')}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          {product.shipment_count?.toLocaleString()}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">
                          {product.total_quantity?.toLocaleString() || 0}
                        </span>
                        {product.primary_unit && (
                          <span className="text-xs text-slate-500 ml-1">{product.primary_unit}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          {product.factory_count}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {product.hs_codes?.map((code, i) => (
                            <span key={i} className="text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              {code}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          {/* Modal Footer */}
          <div className="flex items-center justify-between pt-4 border-t mt-4">
            <p className="text-sm text-slate-500">
              {portProducts.length} products shown
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPortModal(false);
                  navigate('/export/products');
                }}
                className="gap-1"
              >
                View All Products
                <ExternalLink className="w-3 h-3" />
              </Button>
              <Button variant="outline" onClick={() => setShowPortModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportDashboard;
